<script lang="ts">
  import { createEventDispatcher, onMount } from 'svelte';

  interface IOption {
    id: string,
    name: string
  };

  export let label = '';
  export let disabled = false;
  export let isShowSearch = false;
  export let options: Array<IOption> = [];
  export let selectedValue: IOption = {
    id: '',
    name: '',
  };

  let isShowDropdown = false;
  let currentHoverIndex = -1;
  let ulEl: HTMLElement;
  const optionEls: HTMLElement[] = [];

  let searchValue = '';
  let filteredOptions: Array<IOption> = options;
  $: {
    if (searchValue.length === 0) {
      filteredOptions = options;
    } else {
      filteredOptions = options.filter(option => option.name.indexOf(searchValue) > -1);
    }
    currentHoverIndex = filteredOptions.findIndex(option => option.id === selectedValue.id);

    const selectedOptionEl = optionEls.find(el => el?.dataset.id === selectedValue.id);
    selectedOptionEl?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
    });
  }

  const dispatch = createEventDispatcher();

  let isShowInputPlaceHolder = true;
  const handleInputKeyup = (event: KeyboardEvent) => {
    const { key, target } = event;
    const input = target as HTMLInputElement;
    searchValue = input.value;
    isShowInputPlaceHolder = !(input.value.length > 0);
    if (key === 'Enter') {
      isShowDropdown = !isShowDropdown;
    }
  };

  const handleKeyboardEvent = (event: KeyboardEvent) => {
    const { key } = event;
    if (key === 'ArrowUp' && currentHoverIndex > 0) {
      currentHoverIndex -= 1;
    } else if (key === 'ArrowDown' && currentHoverIndex < filteredOptions.length - 1) {
      currentHoverIndex += 1;
    } else if (key === 'Enter' && currentHoverIndex !== -1) {
      isShowDropdown = false;
      selectedValue = filteredOptions[currentHoverIndex];
      dispatch('selectChange', selectedValue);
    }

    const activeOptionEl = optionEls.find((_, index) => index === currentHoverIndex);
    activeOptionEl?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
    });
  };

  onMount(() => {
    window.document.addEventListener('keyup', handleKeyboardEvent);

    return () => {
      window.document.removeEventListener('keyup', handleKeyboardEvent);
    };
  });

</script>

<!-- svelte-ignore a11y-click-events-have-key-events a11y-no-noninteractive-element-interactions a11y-no-static-element-interactions -->

<div class="select-row-wrapper">
  <div class="row-label">
    {label}
  </div>
  <div class="right-wrapper">
    <div class={`select-row ${disabled ? 'disabled' : ''}`}>
      <div class={`select-selection ${isShowDropdown ? 'focused' : ''}`}>
        <div class="select-selection-rendered">
          {#if isShowSearch}
            <div class="select-search">
              <input class="select-search__field"
                value={searchValue}
                on:keyup={handleInputKeyup}
                on:focus={ () => {
                  isShowDropdown = true;
                } }
                on:blur={() => {
                  setTimeout(() => {
                    isShowDropdown = false;
                  }, 300);
                }}
                >
            </div>
          {/if}
          {#if isShowInputPlaceHolder}
            <div class="selected-value">
              {selectedValue.name}
            </div>
          {/if}
        </div>
        <span class="select-arrow" on:click={() => {
          isShowDropdown = !isShowDropdown;
        }}>
          <i class="select-arrow-icon">
            <svg
              viewBox="64 64 896 896"
              data-icon="down"
              width="1em"
              height="1em"
              fill="currentColor"
              aria-hidden="true"
              focusable="false">
                <path d="M884 256h-75c-5.1 0-9.9 2.5-12.9 6.6L512 654.2 227.9 262.6c-3-4.1-7.8-6.6-12.9-6.6h-75c-6.5 0-10.3 7.4-6.5 12.7l352.6 486.1c12.8 17.6 39 17.6 51.7 0l352.6-486.1c3.9-5.3.1-12.7-6.4-12.7z"></path>
              </svg>
          </i>
        </span>
      </div>

      {#if isShowDropdown }
      <div class="select-dropdown">
        <ul class="select-dropdown-menu" on:scroll|stopPropagation bind:this={ulEl}>
          {#if filteredOptions.length > 0}
            {#each filteredOptions as option, index }
              <li
                bind:this={ optionEls[index] }
                class="select-dropdown-menu-item"
                class:active={index === currentHoverIndex}
                class:selected={option.id === selectedValue.id}
                data-id={option.id}
                on:mouseenter={() => {
                  currentHoverIndex = index;
                }}
                on:mouseleave={() => {
                  currentHoverIndex = -1;
                }}
                on:click={() => {
                  isShowDropdown = false;
                  selectedValue = option;
                  dispatch('selectChange', option);
                }}
              >
                {option.name}
              </li>
            {/each}
          {:else }
            <slot name="notFoundContent" />
          {/if}
        </ul>
      </div>
      {/if}
    </div>
  </div>
</div>

<style lang="scss">
  .select-row-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 0;
    .row-label {
      font-family: PingFangSC-Medium;
      font-weight: 500;
      font-size: 16px;
      color: #191919;
    }
    .right-wrapper {
      display: flex;
      width: 80%;
      align-items: center;
      .select-row {
        width: 100%;
        box-sizing: border-box;
        margin: 0;
        padding: 0;
        color: rgba(0, 0, 0, 0.65);
        font-size: 14px;
        font-variant: tabular-nums;
        line-height: 1.5;
        list-style: none;
        font-feature-settings: 'tnum';
        position: relative;
        display: inline-block;
        outline: 0;
        &.disabled {
          color: rgba(0,0,0,0.25);

          > .select-selection {
            background: #f5f5f5;
            cursor: not-allowed;
          }
        }
      }
    }
  }

  .select-selection {
    display: block;
    box-sizing: border-box;
    background-color: #fff;
    border: 1px solid #d9d9d9;
    border-top-width: 1.02px;
    border-radius: 4px;
    outline: none;
    transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
    user-select: none;
    position: relative;
    height: 32px;
    cursor: pointer;

    &.focused {
      opacity: 0.4;
      border-color: rgb(255,220,20);
      border-right-width: 1px !important;
      outline: 0;
      box-shadow: 0 0 0 2px rgba(255,220,20, 0.2);

      .select-arrow-icon svg {
        transform: rotate(180deg);
      }
    }
  }

  .select-selection-rendered {
    height: 100%;
    margin-right: 24px;
    position: relative;
    display: block;
    margin-left: 11px;
    line-height: 30px;
  }

  .selected-value {
    float: left;
    max-width: 100%;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .select-search {
    position: absolute;
    width: 100%;
    height: 100%;
    display: inline-block;
  }

  .select-search__field {
    width: 100%;
    height: 100%;
    font-size: 100%;
    line-height: 1;
    background: transparent;
    border-width: 0;
    border-radius: 4px;
    outline: 0;
  }


  .select-arrow {
    display: inline-block;
    color: inherit;
    font-style: normal;
    line-height: 0;
    text-align: center;
    text-transform: none;
    vertical-align: -0.125em;
    text-rendering: optimizeLegibility;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    position: absolute;
    top: 50%;
    right: 11px;
    margin-top: -6px;
    color: rgba(0, 0, 0, 0.25);
    font-size: 12px;
    line-height: 1;
    transform-origin: 50% 50%;

    > .select-arrow-icon {
      display: block;
      color: inherit;
      font-style: normal;
      text-align: center;
      text-transform: none;

      svg {
        transition: transform 0.3s;
      }

      > * {
        line-height: 1;
      }
    }
  }

  .select-dropdown {
    margin: 5px 0 0 0;
    padding: 0;
    width: 362px;
    color: rgba(0, 0, 0, 0.65);
    font-variant: tabular-nums;
    line-height: 1.5;
    list-style: none;
    font-feature-settings: 'tnum';
    position: absolute;
    z-index: 1050;
    box-sizing: border-box;
    font-size: 14px;
    font-variant: initial;
    background-color: #fff;
    border-radius: 4px;
    outline: none;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }

  .select-dropdown-menu {
    max-height: 250px;
    margin-bottom: 0;
    padding: 4px 0;
    padding-left: 0;
    overflow: auto;
    list-style: none;
    outline: none;
  }

  .select-dropdown-menu-item {
    position: relative;
    display: block;
    padding: 5px 12px;
    overflow: hidden;
    color: rgba(0, 0, 0, 0.65);
    font-weight: normal;
    font-size: 14px;
    line-height: 22px;
    white-space: nowrap;
    text-overflow: ellipsis;
    cursor: pointer;
    transition: background 0.3s ease;
    &.selected {
      color: rgba(0, 0, 0, 0.65);
      font-weight: 600;
      background-color: #fafafa;
    }
    &.active {
      background-color: rgba(255,220,20, 0.2);
    }
  }

  .default-panel {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding-bottom: 10px;
    .default-text {
      font-family: PingFangSC-Regular;
      font-weight: 400;
      font-size: 14px;
      color: rgba(25,25,25,0.50);
    }
  }
  </style>

